
import { db, storage, auth } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { UserProfile, UserRole } from '../../types';

export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
    try {
        const snap = await db.collection('users').doc(uid).get();
        if (snap.exists) return snap.data() as UserProfile;
    } catch(e) {
        console.warn("Failed to fetch user profile", e);
    }
    return null;
};

// NEW: Resolve Slug to Profile
export const getUserBySlug = async (slug: string): Promise<UserProfile | null> => {
    try {
        const cleanSlug = slug.toLowerCase().trim();
        const snap = await db.collection('users').where('slug', '==', cleanSlug).limit(1).get();
        if (!snap.empty) {
            return snap.docs[0].data() as UserProfile;
        }
    } catch (e) {
        console.warn("Failed to resolve slug", e);
    }
    return null;
};

// NEW: Check Availability
export const checkSlugAvailability = async (slug: string): Promise<boolean> => {
    const cleanSlug = slug.toLowerCase().trim();
    // Reserved words
    if (['admin', 'dashboard', 'login', 'register', 'api'].includes(cleanSlug)) return false;
    
    const snap = await db.collection('users').where('slug', '==', cleanSlug).limit(1).get();
    return snap.empty;
};

export const getAllUsers = async (): Promise<UserProfile[]> => {
  const currentUser = auth.currentUser;
  if (!currentUser) return [];

  // Fetch current user's profile to check role
  const profileSnap = await db.collection('users').doc(currentUser.uid).get();
  const profile = profileSnap.data() as UserProfile;

  let query = db.collection('users');

  // STRICT ISOLATION LOGIC
  if (profile.role === 'SUPER_ADMIN') {
      // Super Admin sees everyone
      const snap = await query.get();
      return snap.docs.map((d: any) => d.data() as UserProfile);
  } 
  else if (profile.role === 'OWNER') {
      // Owners see: Themselves + Their Staff (Managers/Artists) + Their Clients
      // We filter by 'managedBy' or 'tenantId' if available, or just fetch staff roles
      // IMPORTANT: Exclude SUPER_ADMIN explicitly
      const staffSnap = await db.collection('users').where('role', 'in', ['OWNER', 'ARTIST', 'MANAGER']).get();
      const clientSnap = await db.collection('users').where('role', '==', 'CLIENT').get();
      
      const users = [...staffSnap.docs, ...clientSnap.docs].map((d: any) => d.data() as UserProfile);
      
      // Client-side filter to ensure we don't accidentally show other tenants if database rules allow read
      // We include clients where their tenantId matches owner's uid, or managedBy matches owner's uid
      return users.filter(u => u.uid === currentUser.uid || u.managedBy === currentUser.uid || u.tenantId === currentUser.uid || u.studioId === currentUser.uid);
  }
  else {
      // Staff see: Themselves + Their Manager (Owner) + Colleagues + Clients
      const staffSnap = await db.collection('users').where('role', 'in', ['OWNER', 'ARTIST', 'MANAGER']).get();
      const clientSnap = await db.collection('users').where('role', '==', 'CLIENT').get();
      
      const users = [...staffSnap.docs, ...clientSnap.docs].map((d: any) => d.data() as UserProfile);
      
      const ownerId = profile.managedBy || profile.tenantId || profile.studioId || currentUser.uid;
      return users.filter(u => u.uid === ownerId || u.managedBy === ownerId || u.uid === currentUser.uid || u.tenantId === ownerId || u.studioId === ownerId);
  }
};

// Admin function to fetch ALL clients (e.g. for marketing), regulated by security rules
export const getAllClients = async (): Promise<UserProfile[]> => {
    const currentUser = auth.currentUser;
    if (!currentUser) return [];
    
    const profileSnap = await db.collection('users').doc(currentUser.uid).get();
    const profile = profileSnap.data() as UserProfile;

    // If Super Admin, fetch all
    if (profile.role === 'SUPER_ADMIN') {
        const snap = await db.collection('users').where('role', '==', 'CLIENT').get();
        return snap.docs.map((d: any) => d.data() as UserProfile);
    }

    // If Tenant, fetch only clients assigned to them or their artists
    // Note: Firestore doesn't support logical OR in where clauses easily for this specific structure
    // so we rely on Security Rules to filter the 'list' request, 
    // or we fetch based on 'assignedArtistId' if we track that.
    
    // For now, fetch clients and rely on Security Rules 'allow list' to filter visibility
    const snap = await db.collection('users').where('role', '==', 'CLIENT').get();
    return snap.docs.map((d: any) => d.data() as UserProfile);
};

export const getPublicStudios = async (): Promise<UserProfile[]> => {
    const snap = await db.collection('users').where('role', 'in', ['OWNER', 'ARTIST']).get();
    const users = snap.docs.map((d: any) => d.data() as UserProfile);
    return users.filter(u => !u.hidePublicProfile);
};

export const getTeamMembers = async (managerId: string): Promise<UserProfile[]> => {
    const snap = await db.collection('users').where('managedBy', '==', managerId).get();
    return snap.docs.map((d: any) => d.data() as UserProfile);
};

export const getClientsForArtist = async (artistId: string): Promise<UserProfile[]> => {
    const snap = await db.collection('users')
        .where('role', '==', 'CLIENT')
        .where('assignedArtistId', '==', artistId)
        .get();
    return snap.docs.map((d: any) => d.data() as UserProfile);
};

export const updateUserRole = async (uid: string, role: UserRole) => {
  await db.collection('users').doc(uid).update({ role });
};

export const updateUserProfile = async (uid: string, data: Partial<UserProfile>) => {
    await db.collection('users').doc(uid).update(data);
};

// Safe update for nested Google Calendar fields using Dot Notation
// This prevents overwriting other googleCalendar fields like 'refreshToken' or 'lastSyncAt'
export const updateUserGoogleConfig = async (uid: string, updates: Record<string, any>) => {
    const safeUpdates: Record<string, any> = {};
    for (const [key, value] of Object.entries(updates)) {
        safeUpdates[`googleCalendar.${key}`] = value;
    }
    await db.collection('users').doc(uid).update(safeUpdates);
};

export const uploadUserBranding = async (userId: string, file: File): Promise<string> => {
    const storageRef = ref(storage, `users/${userId}/branding/${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
};
